/**
 * R-16 T-135 (design v1.2 change 02 §4) — the ▾ buttons beside the Apps, Data and Admin platform entries. The entries
 * stay links to their landing pages; the ▾ (and hovering the link with intent) opens a menu: Apps lists the installed
 * apps from GET /api/apps with their one-line purpose and "All apps"; Data lists the platform's data pages; Admin lists
 * the Admin pages the person may open, grouped by section, then each app's own settings by app. Admin is absent for a
 * non-admin (the declaration carries no Admin entry for them). Each ▾ reads its disclosure row (nav.l0.<id>.menu).
 */
import { type RefObject, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button, Menu, MenuColumns, MenuGroup, MenuItem, MenuSeparator, menuColumns, useDisclosure } from '@/kit';
import { appOpenPath, type MenuDecl, type MenuItemDecl, type PublishedApp } from '@/features/apps/apps-api';

export const L0_MENU_IDS = ['apps', 'data', 'admin'] as const;
type L0MenuId = (typeof L0_MENU_IDS)[number];

const LABEL: Record<L0MenuId, string> = { apps: 'Apps menu', data: 'Data menu', admin: 'Admin menu' };

interface Sources {
  apps: readonly PublishedApp[];
  dataSections: readonly MenuItemDecl[];
  adminGroups: readonly MenuDecl[];
}

/** The Admin ▾ columns: the platform's groups by section, then each app's settings pages under the app's name. */
export function adminColumns(groups: readonly MenuDecl[], apps: readonly PublishedApp[]): MenuItemDecl[] {
  const platform = groups.flatMap((g) => g.items.map((it) => ({ ...it, group: g.section ?? g.label })));
  const own = apps.flatMap((a) => ((a.adminPages ?? []) as MenuItemDecl[]).map((p) => ({ ...p, group: a.name })));
  return [...platform, ...own];
}

function Columns({ items, testPrefix }: { items: readonly MenuItemDecl[]; testPrefix: string }) {
  const cols = menuColumns(items);
  return (
    <MenuColumns count={cols.length}>
      {cols.map((c) => (
        <MenuGroup key={c.title ?? '-'} title={c.title}>
          {c.items.map((it) => (
            <MenuItem key={`${c.title}-${it.id}`} render={<Link to={it.route} />} hint={it.hint} data-testid={`${testPrefix}-${it.id}`}>
              {it.label}
            </MenuItem>
          ))}
        </MenuGroup>
      ))}
    </MenuColumns>
  );
}

function L0Menu({ id, entry, sources, linkRef }: { id: L0MenuId; entry: MenuItemDecl; sources: Sources; linkRef: RefObject<HTMLAnchorElement | null> }) {
  const shown = useDisclosure(`nav.l0.${id}.menu`);
  if (!shown) return null;
  const body = {
    apps: () => (
      <>
        <Columns items={sources.apps.map((a) => ({ id: a.slug, label: a.name, route: appOpenPath(a), hint: a.description?.trim() || undefined }))} testPrefix="l0-app" />
        <MenuSeparator />
        <MenuItem render={<Link to={entry.route} />} data-testid="l0-apps-all">
          All apps
        </MenuItem>
      </>
    ),
    data: () => <Columns items={sources.dataSections} testPrefix="l0-data" />,
    admin: () => <Columns items={adminColumns(sources.adminGroups, sources.apps)} testPrefix="l0-admin" />,
  }[id]();
  return (
    <Menu
      hover
      mega
      hoverRef={linkRef}
      aria-label={entry.label}
      trigger={
        <Button icon aria-label={LABEL[id]} className="k-l0-caret" data-testid={`platform-nav-${id}-menu`}>
          <ChevronDown aria-hidden />
        </Button>
      }
    >
      {body}
    </Menu>
  );
}

/** A platform entry: its link, and for Apps, Data and Admin the ▾ beside it. */
export function L0Entry({ entry, sources }: { entry: MenuItemDecl; sources: Sources }) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const menu = (L0_MENU_IDS as readonly string[]).includes(entry.id) ? (entry.id as L0MenuId) : null;
  const link = (
    <NavLink ref={ref} to={entry.route} end={entry.id === 'apps'} data-testid={`platform-nav-${entry.id}`} className={({ isActive }) => (isActive ? 'text-bar-ink' : 'text-bar-ink-2')}>
      {entry.label}
    </NavLink>
  );
  if (!menu) return link;
  return (
    <span className="k-l0-split">
      {link}
      <L0Menu id={menu} entry={entry} sources={sources} linkRef={ref} />
    </span>
  );
}
